"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PLAN_THIN_RATIO = exports.NO_RECENT_LOGS_WORKING_DAYS = exports.STALE_PROGRESS_WORKING_DAYS = void 0;
exports.scanProject = scanProject;
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C57 · the scan. One project's data in, findings out, and nothing else.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **PURE, AND THAT IS WHY IT IS HERE RATHER THAN IN A SERVICE.** Twenty-six predicates over
 * a snapshot is the part that is expensive to get wrong and cheap to test: every rule can be
 * shown to fire on exactly the shape it is about, with no database and no fixtures. The service's
 * job is to LOAD the snapshot; this file decides nothing about where the rows come from.
 *
 * ⚠️⚠️ **A FINDING CARRIES FACTS, NOT A SENTENCE.** The mockup composes each one's prose inline —
 * *"Anna tagja a projektnek, de nincs hozzá érvényes díjkártya…"*. Rule 7 does not allow that, so
 * a finding carries its subject and its figures and the screen composes with `alert.<kind>.*`.
 * It also means the same finding reads correctly in both languages, which an inlined sentence
 * cannot.
 *
 * ⚠️ **`key` is what makes a finding the SAME finding across scans.** C58 persists these, and a
 * lifecycle needs identity: `(project, kind, key)` is it. A key that moved between scans would
 * close a finding and open an identical one every time somebody ran it.
 */
/** A number of hours below which a figure counts as nothing — the mockup's own `0.004`. */
const EPSILON = 0.004;
/* ── the thresholds ────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️⚠️ **HARDCODED, AND P5-3 RULED THAT DELIBERATELY.** *"Constants in code, revisited after real
 * data. No settings row, no migration, no UI."* What was NOT deferred is the unit above: the
 * number is one edit here, and the unit decides what the query joins.
 */
exports.STALE_PROGRESS_WORKING_DAYS = 14;
exports.NO_RECENT_LOGS_WORKING_DAYS = 14;
exports.PLAN_THIN_RATIO = 0.5;
/* ── the scan ──────────────────────────────────────────────────────────────────────────────── */
function scanProject(snapshot) {
    const found = [];
    const add = (kind, key, subjectType, subjectId, subjectLabel, values = {}) => {
        found.push({ kind, key, subjectType, subjectId, subjectLabel, values });
    };
    const hours = (value) => value.toFixed(2);
    /* ── the people on it ────────────────────────────────────────────────────────────────────── */
    for (const member of snapshot.members) {
        if (!member.active)
            continue;
        if (!member.hasRateCard) {
            add('member_no_rate', member.memberId, 'member', member.memberId, member.name);
        }
        if (!member.hasSchedule) {
            add('member_no_sched', member.memberId, 'member', member.memberId, member.name);
        }
    }
    /**
     * ⚠️ **An INACTIVE member who still has hours**, which is a different rule from the two above:
     * those are about somebody who is here and unpriced, this is about somebody who is gone and
     * still counted.
     */
    for (const member of snapshot.members) {
        if (member.active)
            continue;
        const logged = snapshot.worklogs
            .filter((worklog) => worklog.memberId === member.memberId)
            .reduce((total, worklog) => total + worklog.hours, 0);
        if (logged > EPSILON) {
            add('inactive_member_log', member.memberId, 'member', member.memberId, member.name, {
                hours: hours(logged),
            });
        }
    }
    /* ── P2-32.5 · the issues nothing claimed ────────────────────────────────────────────────── */
    for (const issue of snapshot.unroutedIssues) {
        add('task_unrouted', issue.taskId, 'task', issue.taskId, `${issue.key} · ${issue.title}`, {
            remote: issue.remoteProjectKey,
        });
    }
    /* ── the tasks ───────────────────────────────────────────────────────────────────────────── */
    for (const task of snapshot.tasks) {
        const label = `${task.key} · ${task.title}`;
        /* ⚠️ Only a task somebody has LOGGED against: an unstarted task with no line item is
           ordinary, and flagging it would bury the rule that matters under the backlog. */
        if (snapshot.contractsEnabled && task.lineItemId === null && task.loggedHours > EPSILON) {
            add('task_no_line', task.key, 'task', task.id, label);
        }
        if (task.syncProblem === 'orphaned') {
            add('task_orphaned', task.key, 'task', task.id, label);
        }
        if (task.matchingLineItems > 1) {
            add('task_multi_line', task.key, 'task', task.id, label, {
                count: String(task.matchingLineItems),
            });
        }
        if (task.assigneeMemberId === null) {
            add('task_no_assignee', task.key, 'task', task.id, label);
        }
        if (!task.exemptFromPlanning) {
            if (task.estimateHours <= EPSILON) {
                add('task_no_estimate', task.key, 'task', task.id, label);
            }
            if (task.startsOn === null || task.dueOn === null) {
                add('task_no_dates', task.key, 'task', task.id, label);
            }
        }
        /**
         * ⚠️⚠️ **WORKING DAYS, NOT CALENDAR DAYS — P5-3.** A task nobody has touched over a
         * fortnight's shutdown is not stuck; it is August.
         */
        /* ⚠️ Work that has STARTED: in progress or waiting on a review. A task in review with no
           activity for a fortnight is stuck in exactly the way this rule is about. */
        if ((task.statusCategory === 'in_progress' || task.statusCategory === 'in_review') &&
            !task.exemptFromPlanning &&
            task.lastLoggedOn !== null &&
            /* ⚠️ The ASSIGNEE's working days — see `workingDaysSince`. A task nobody owns falls back
               to the workspace's calendar, which is the only answer available for it. */
            snapshot.workingDaysSince(task.lastLoggedOn, task.assigneeMemberId) >
                exports.STALE_PROGRESS_WORKING_DAYS) {
            add('stale_progress', task.key, 'task', task.id, label, {
                days: String(snapshot.workingDaysSince(task.lastLoggedOn, task.assigneeMemberId)),
            });
        }
        /* ⚠️ `open` is our *not started yet*. Hours on it mean somebody worked without moving it. */
        if (task.statusCategory === 'open' && task.loggedHours > EPSILON) {
            add('log_on_todo', task.key, 'task', task.id, label, { hours: hours(task.loggedHours) });
        }
        /* ⚠️ A dependency is broken when this task STARTS before the one it waits on is DUE. */
        for (const dependency of task.dependsOn) {
            const blocker = snapshot.tasks.find((candidate) => candidate.id === dependency.taskId);
            if (blocker?.dueOn != null && task.startsOn !== null && task.startsOn < blocker.dueOn) {
                add('dep_date_violation', `${task.key}>${dependency.key}`, 'task', task.id, `${task.key} → ${dependency.key}`, { blocker: dependency.key });
            }
        }
    }
    /* ── the deliverables ────────────────────────────────────────────────────────────────────── */
    if (snapshot.contractsEnabled) {
        for (const item of snapshot.lineItems) {
            const hasWork = item.type !== 'license';
            /* ⚠️ Only one that has been worked: a frame nobody has spent against is not yet wrong. */
            if (hasWork &&
                item.type !== 'non_billable' &&
                item.trackedHours > EPSILON &&
                (item.budget === null || item.budget <= 0)) {
                add('li_no_budget', item.id, 'line_item', item.id, item.label);
            }
            if (item.startsOn === null || item.endsOn === null) {
                add('li_no_window', item.id, 'line_item', item.id, item.label);
            }
            if (item.type === 'time_and_material' && (item.rate === null || item.rate <= 0)) {
                add('li_no_rate_tm', item.id, 'line_item', item.id, item.label);
            }
            /* ⚠️ A licence earns per period; a period that has passed with no invoice is unbilled. */
            if (!hasWork && item.pastPeriods > item.invoiceCount) {
                add('license_not_invoiced', item.id, 'line_item', item.id, item.label, {
                    periods: String(item.pastPeriods - item.invoiceCount),
                });
            }
            if (item.revenue !== null && item.invoicedTotal > item.revenue) {
                add('over_invoiced', item.id, 'line_item', item.id, item.label, {
                    over: (item.invoicedTotal - item.revenue).toFixed(2),
                });
            }
            /**
             * ⚠️ **`plan_thin` is about the FRAME, not about the hours worked.** A deliverable whose
             * resource plan covers less than half its estimated hours is a forecast that cannot come
             * true — which is a different complaint from being behind.
             *
             * ⚠️⚠️ **HOURS AGAINST HOURS.** It compared the plan with `budget`, which is money; the
             * mockup's `est` is the estimate. The loader windows both sides alike (see `frameHours`).
             */
            if (hasWork &&
                item.frameHours > EPSILON &&
                item.allocatedHours < item.frameHours * exports.PLAN_THIN_RATIO) {
                add('plan_thin', item.id, 'line_item', item.id, item.label, {
                    planned: hours(item.allocatedHours),
                    frame: hours(item.frameHours),
                });
            }
        }
        /**
         * ⚠️⚠️ **ux-pass-2 · ONE FINDING PER LINE ITEM, NOT PER HOUR.** Closing an invoiced phase and
         * then receiving the last morning's backdated hours is one event; a finding per worklog would
         * cost the RISK score three times over for it. The figures say how much arrived.
         */
        for (const closed of snapshot.closedLineItems) {
            if (closed.tasksAfterClose > 0 || closed.hoursAfterClose > EPSILON) {
                add('li_closed_work', closed.id, 'line_item', closed.id, closed.label, {
                    closedOn: closed.closedOn,
                    tasks: String(closed.tasksAfterClose),
                    hours: hours(closed.hoursAfterClose),
                });
            }
        }
        for (const contract of snapshot.contracts) {
            if (contract.endsOn !== null && contract.loggedAfterEnd > EPSILON) {
                add('contract_expired_log', contract.id, 'contract', contract.id, contract.label, {
                    hours: hours(contract.loggedAfterEnd),
                });
            }
        }
    }
    /* ── the hours ───────────────────────────────────────────────────────────────────────────── */
    /**
     * ⚠️⚠️ **A DUPLICATE IS THE SECOND AND LATER OF AN IDENTICAL SIGNATURE, NOT BOTH.** Flagging
     * both halves of a pair would double the count and, through the RISK cap, misprice it.
     */
    const seen = new Set();
    for (const worklog of snapshot.worklogs) {
        /* ⚠️ `toFixed(2)` rather than the raw number: `4` and `4.0` are the same hour and must
           produce the same signature, or a duplicate typed differently reads as two entries. */
        const signature = `${worklog.taskId}|${worklog.memberId}|${worklog.workedOn}|${worklog.hours.toFixed(2)}`;
        if (seen.has(signature)) {
            add('wlog_duplicate', 
            /**
             * ⚠️⚠️ **THE WORKLOG'S OWN ID, AND THE SIGNATURE WAS A BUG THE SEED FOUND.** Keying on
             * the signature is fine for a PAIR and breaks on a triple: the second and third entries
             * are both *"second and later"*, both fire, and both carry the same key — so
             * `alert_findings_identity` refuses the second insert and the whole project's scan throws.
             * It surfaced as one project in eight failing to scan at all, which is the worst shape:
             * the chore reported a failure and the tab stayed empty.
             *
             * ⚠️ **Stable across scans, which is what C58's lifecycle needs.** A worklog's id does not
             * move, so the finding keeps its `first_detected_at` and resolves when the row goes.
             */
            worklog.id, 'worklog', worklog.id, `${worklog.memberName} · ${worklog.workedOn} · ${worklog.taskKey}`, { hours: hours(worklog.hours) });
        }
        seen.add(signature);
    }
    for (const worklog of snapshot.worklogs) {
        if (worklog.lineItemId === null)
            continue;
        const item = snapshot.lineItems.find((candidate) => candidate.id === worklog.lineItemId);
        if (item === undefined)
            continue;
        const before = item.startsOn !== null && worklog.workedOn < item.startsOn;
        const after = item.endsOn !== null && worklog.workedOn > item.endsOn;
        if (before || after) {
            add('wlog_outside_li', worklog.id, 'worklog', worklog.id, `${worklog.memberName} · ${worklog.workedOn}`, { lineItem: item.label });
        }
        /* ⚠️ Only on a recurring contract — a one-off has no periods for an hour to miss. */
        if (!worklog.recurring)
            continue;
        if (worklog.occurrenceCount === 0) {
            add('wlog_no_occ', worklog.id, 'worklog', worklog.id, `${worklog.memberName} · ${worklog.workedOn}`, { lineItem: item.label });
        }
        else if (worklog.occurrenceCount > 1) {
            add('wlog_multi_occ', worklog.id, 'worklog', worklog.id, `${worklog.memberName} · ${worklog.workedOn}`, { count: String(worklog.occurrenceCount) });
        }
    }
    /**
     * ⚠️⚠️ **OS-1 · ONE FINDING PER PERSON, NOT PER HOUR.** Somebody logging on a bucket ticket that
     * no line item names them on is one missing name, or one window that ends too early — a finding
     * per day would read as twenty problems and, through the cap, cost the RISK score its whole
     * worklog share for one person's month. The figures say how much, and over which days.
     */
    if (snapshot.contractsEnabled) {
        const unidentified = new Map();
        for (const worklog of snapshot.worklogs) {
            if (worklog.lineItemId !== null || worklog.taskLineItemId === null)
                continue;
            const current = unidentified.get(worklog.memberId);
            unidentified.set(worklog.memberId, {
                name: worklog.memberName,
                hours: (current?.hours ?? 0) + worklog.hours,
                from: current === undefined || worklog.workedOn < current.from
                    ? worklog.workedOn
                    : current.from,
                to: current === undefined || worklog.workedOn > current.to ? worklog.workedOn : current.to,
            });
        }
        for (const [memberId, row] of unidentified) {
            if (row.hours <= EPSILON)
                continue;
            add('wlog_unidentified', memberId, 'member', memberId, row.name, {
                hours: hours(row.hours),
                from: row.from,
                to: row.to,
            });
        }
    }
    /* ── the money around it ─────────────────────────────────────────────────────────────────── */
    for (const cost of snapshot.externalCosts) {
        if (!cost.linked) {
            add('ext_cost_unlinked', cost.id, 'external_cost', cost.id, cost.label);
        }
    }
    if (snapshot.contractsEnabled) {
        /**
         * ⚠️ **One finding per PERSON, not per allocation.** Somebody booked across six deliverables
         * with no rate card is one missing rate card; six findings would read as six problems and,
         * through the cap, cost double what one does.
         */
        const unpriced = new Map();
        for (const allocation of snapshot.allocations) {
            if (allocation.hasRateCard)
                continue;
            const current = unpriced.get(allocation.memberId);
            unpriced.set(allocation.memberId, {
                name: allocation.memberName,
                hours: (current?.hours ?? 0) + allocation.hours,
            });
        }
        for (const [memberId, row] of unpriced) {
            add('alloc_no_rate', memberId, 'member', memberId, row.name, { hours: hours(row.hours) });
        }
        for (const code of snapshot.currenciesWithoutRate) {
            add('fx_missing', code, 'currency', code, code);
        }
    }
    /* ── the project itself ──────────────────────────────────────────────────────────────────── */
    /**
     * ⚠️ **Only an ACTIVE project with tasks on it.** A project on hold with nothing logged is not
     * a data problem, and one with no tasks has nothing to log against yet.
     */
    if (snapshot.projectActive &&
        snapshot.tasks.length > 0 &&
        snapshot.lastLoggedOn !== null &&
        /* ⚠️ `null`: a PROJECT does not take leave, so this is the workspace's calendar alone. */
        snapshot.workingDaysSince(snapshot.lastLoggedOn, null) > exports.NO_RECENT_LOGS_WORKING_DAYS) {
        add('no_recent_logs', 'project', 'project', snapshot.projectId, snapshot.projectName, {
            days: String(snapshot.workingDaysSince(snapshot.lastLoggedOn, null)),
        });
    }
    /**
     * ── EV-8 · the five EACs' data gaps ──────────────────────────────────────────────────────────
     *
     * ⚠️ **One finding per kind per project**, keyed `project` like `no_recent_logs`: the evaluation
     * measures the whole project, so a share or a count is what it can say. `fxMissing` is skipped —
     * `fx_missing` above already names the currency.
     */
    const share = (value) => `${String(Math.round(value * 100))}%`;
    const onProject = (kind, values) => {
        add(kind, 'project', 'project', snapshot.projectId, snapshot.projectName, values);
    };
    for (const one of snapshot.eacFlags?.red ?? []) {
        if (one.flag === 'unestimatedHours')
            onProject('wlog_unestimated_share', { share: share(one.value) });
        else if (one.flag === 'unboundHours')
            onProject('wlog_unbound_share', { share: share(one.value) });
        else if (one.flag === 'itemsWithoutTasks')
            onProject('li_no_tasks', { count: String(one.value) });
        else if (one.flag === 'reestimatedClosed') {
            onProject('task_reestimated_closed', { count: String(one.value) });
        }
        else
            onProject('li_rule_on_status', { count: String(one.value) });
    }
    for (const one of snapshot.eacFlags?.yellow ?? []) {
        if (one.flag === 'staleAllocation')
            onProject('alloc_stale', { share: share(one.value) });
        else if (one.flag === 'itemOverPlan')
            onProject('li_over_plan', { ratio: one.value.toFixed(1) });
        else if (one.flag === 'silentPeople')
            onProject('member_silent', { count: String(one.value) });
    }
    return found;
}
//# sourceMappingURL=scan.js.map